import React, {useState} from 'react';
import {View} from 'react-native';

import CCText from '../../../Atoms/CCText';
import type {CourseCover as Cover} from '../../../Hooks/LmsCourseApi/types';
import {F} from '../courseTheme';
import CourseCover from './CourseCover';

type Props = {
  title: string;
  /** AI artwork. Absent, or failing to load, shows the designed tile. */
  thumbnail?: string | null;
  /** Over the top-left corner — a status or a label. */
  topLeft?: React.ReactNode;
  /** Over the top-right corner — the duration, say. */
  topRight?: React.ReactNode;
  /** Hide the overlaid title where the page already shows it. */
  showTitle?: boolean;
  radius?: number;
  /**
   * The course's composed thumbnail (title, a puzzle, the coach). When
   * present it replaces the plain artwork-plus-title; the corner chips stay.
   */
  cover?: Cover | null;
};

/**
 * A course's cover: the AI artwork, or — when there is none, or it will not
 * load — a tile in the brand colour. The title is overlaid either way; the
 * artwork is made without text on purpose, because image models garble
 * lettering.
 */
export default function CourseArtwork({
  title,
  thumbnail,
  topLeft,
  topRight,
  showTitle = true,
  radius = 0,
  cover = null,
}: Props) {
  const [broken, setBroken] = useState(false);
  const [width, setWidth] = useState(0);
  const art = !!thumbnail && !broken;

  return (
    <View
      testID="course-artwork"
      onLayout={e => setWidth(Math.round(e.nativeEvent.layout.width))}
      style={{
        width: '100%',
        aspectRatio: 16 / 9,
        backgroundColor: F.tealDark,
        borderRadius: radius,
        overflow: 'hidden',
        position: 'relative',
      }}>
      {cover ? (
        <View style={{position: 'absolute', top: 0, left: 0}}>
          <CourseCover cover={cover} thumbnail={art ? thumbnail : null} width={width} />
        </View>
      ) : null}
      {art && !cover ? (
        <img
          src={thumbnail as string}
          alt=""
          onError={() => setBroken(true)}
          style={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover'}}
        />
      ) : null}
      {/* Keeps white text legible over any artwork. */}
      <View
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
          backgroundColor: art && !cover ? 'rgba(0,0,0,0.28)' : 'transparent',
        }}
      />
      <View
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          right: 12,
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}>
        <View>{topLeft ?? null}</View>
        <View>{topRight ?? null}</View>
      </View>
      {showTitle && !cover ? (
        <View style={{position: 'absolute', left: 16, right: 16, bottom: 14}}>
          <CCText
            numberOfLines={3}
            style={{fontSize: 20, fontWeight: '700', color: F.white, lineHeight: 26}}>
            {title}
          </CCText>
        </View>
      ) : null}
    </View>
  );
}
